// Helpdesk admin taxonomy — Iter 107 Phase 2b.
//
// Three sibling editors (Departments, Priorities, Tiers) that share the
// same `/api/helpdesk/settings` endpoint. Each editor is self-contained
// so Settings.jsx can mount them independently as sub-tabs.

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { X, Plus, Trash2, ChevronDown } from "lucide-react";

function useHelpdeskSettings() {
  const [data, setData] = useState(null);
  const load = async () => {
    const { data } = await api.get("/helpdesk/settings");
    setData(data);
  };
  useEffect(() => { load(); }, []);
  return { data, setData, reload: load };
}

// -----------------------------------------------------------------------
// Departments editor
// -----------------------------------------------------------------------
export function HelpdeskDepartmentsAdmin({ canManage }) {
  const { data, setData } = useHelpdeskSettings();
  const [input, setInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!data) return <SkeletonPane />;

  const list = data.departments || [];
  const setList = (next) => { setData({ ...data, departments: next }); setDirty(true); };

  const addFromInput = () => {
    const bits = input.split(",").map((s) => s.trim()).filter(Boolean);
    if (!bits.length) return;
    const merged = [...list];
    bits.forEach((b) => { if (!merged.some((m) => m.toLowerCase() === b.toLowerCase())) merged.push(b); });
    setList(merged);
    setInput("");
  };
  const remove = (idx) => setList(list.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put("/helpdesk/settings/departments", { departments: list });
      setData(fresh);
      setDirty(false);
      toast.success("Departments saved");
    } catch { toast.error("Could not save departments"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    const { data: fresh } = await api.get("/helpdesk/settings");
    setData(fresh);
    setDirty(false);
  };

  return (
    <SectionCard title="Departments"
                 subtitle="Ops teams that tickets can be routed to."
                 dirty={dirty} canManage={canManage}
                 onSave={save} onReset={reset} saving={saving}
                 testid="hd-departments">
      <div className="flex flex-wrap gap-2">
        {list.map((d, i) => (
          <span key={`${d}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700"
                data-testid={`hd-dept-${i}`}>
            <ChevronDown className="h-3 w-3 -rotate-90 opacity-50" />
            {d}
            {canManage && (
              <button className="rounded p-0.5 text-indigo-500 hover:bg-indigo-100" onClick={() => remove(i)}
                      aria-label={`remove ${d}`} data-testid={`hd-dept-rm-${i}`}>
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      {canManage && (
        <div className="mt-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFromInput(); } }}
                 placeholder="TechOps, Engineering…" className="max-w-xl"
                 data-testid="hd-dept-input" />
          <Button size="sm" onClick={addFromInput} disabled={!input.trim()} data-testid="hd-dept-add">
            Add
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

// -----------------------------------------------------------------------
// Priorities editor
// -----------------------------------------------------------------------
export function HelpdeskPrioritiesAdmin({ canManage }) {
  const { data, setData } = useHelpdeskSettings();
  const [levelsInput, setLevelsInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!data) return <SkeletonPane />;

  const rows = data.priorities || [];
  const setRows = (next) => { setData({ ...data, priorities: next }); setDirty(true); };

  const addLevels = () => {
    const bits = levelsInput.split(",").map((s) => s.trim()).filter(Boolean);
    if (!bits.length) return;
    const merged = [...rows];
    bits.forEach((k) => {
      if (!merged.some((r) => r.key.toLowerCase() === k.toLowerCase())) {
        merged.push({ key: k, description: "", sla_target_hours: 0, active: true });
      }
    });
    setRows(merged);
    setLevelsInput("");
  };
  const rmRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const patchRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        key: r.key, description: r.description || "",
        sla_target_hours: Number(r.sla_target_hours) || 0,
        active: r.active !== false,
      }));
      const { data: fresh } = await api.put("/helpdesk/settings/priorities", { priorities: payload });
      setData(fresh);
      setDirty(false);
      toast.success("Priorities saved");
    } catch { toast.error("Could not save priorities"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    const { data: fresh } = await api.get("/helpdesk/settings");
    setData(fresh);
    setDirty(false);
  };

  return (
    <SectionCard title="Priorities"
                 subtitle="P1–P3 with a written description per level. SLA target hours are editable below."
                 dirty={dirty} canManage={canManage}
                 onSave={save} onReset={reset} saving={saving}
                 testid="hd-priorities">
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase text-slate-400">Levels</div>
        <div className="flex flex-wrap gap-2">
          {rows.map((r, i) => (
            <span key={`${r.key}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700"
                  data-testid={`hd-prio-chip-${r.key}`}>
              <ChevronDown className="h-3 w-3 -rotate-90 opacity-50" />
              {r.key}
              {canManage && (
                <button className="rounded p-0.5 text-rose-500 hover:bg-rose-100" onClick={() => rmRow(i)}
                        aria-label={`remove ${r.key}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {canManage && (
          <div className="mt-2 flex gap-2">
            <Input value={levelsInput} onChange={(e) => setLevelsInput(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLevels(); } }}
                   placeholder="P1, P2, P3" className="max-w-xl"
                   data-testid="hd-prio-levels" />
            <Button size="sm" onClick={addLevels} disabled={!levelsInput.trim()} data-testid="hd-prio-add">
              Add
            </Button>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="mb-1 text-[10px] font-semibold uppercase text-slate-400">Descriptions</div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={`${r.key}-desc-${i}`} className="grid grid-cols-12 items-center gap-2"
                 data-testid={`hd-prio-row-${r.key}`}>
              <div className="col-span-1 text-xs font-semibold text-rose-600">{r.key}</div>
              <Input className="col-span-9 h-9" value={r.description}
                     onChange={(e) => patchRow(i, { description: e.target.value })}
                     placeholder="Describe when this level applies…"
                     disabled={!canManage}
                     data-testid={`hd-prio-desc-${r.key}`} />
              <div className="col-span-2 flex items-center gap-1">
                <Input type="number" min={0} step="0.5" className="h-9"
                       value={r.sla_target_hours}
                       onChange={(e) => patchRow(i, { sla_target_hours: e.target.value })}
                       disabled={!canManage}
                       data-testid={`hd-prio-hrs-${r.key}`} />
                <span className="text-[10px] font-semibold uppercase text-slate-400">H</span>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <p className="text-xs text-slate-400">No priority levels yet — add some above.</p>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------
// Tiers editor
// -----------------------------------------------------------------------
export function HelpdeskTiersAdmin({ canManage }) {
  const { data, setData } = useHelpdeskSettings();
  const [levelsInput, setLevelsInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!data) return <SkeletonPane />;

  const rows = data.tiers || [];
  const setRows = (next) => { setData({ ...data, tiers: next }); setDirty(true); };

  const addLevels = () => {
    const bits = levelsInput.split(",").map((s) => s.trim()).filter(Boolean);
    if (!bits.length) return;
    const merged = [...rows];
    bits.forEach((k) => {
      if (!merged.some((r) => r.key.toLowerCase() === k.toLowerCase())) {
        merged.push({ key: k, role_label: "Human", is_ai: false });
      }
    });
    setRows(merged);
    setLevelsInput("");
  };
  const rmRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const patchRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        key: r.key, role_label: r.role_label || "Human", is_ai: !!r.is_ai,
      }));
      const { data: fresh } = await api.put("/helpdesk/settings/tiers", { tiers: payload });
      setData(fresh);
      setDirty(false);
      toast.success("Tiers saved");
    } catch { toast.error("Could not save tiers"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    const { data: fresh } = await api.get("/helpdesk/settings");
    setData(fresh);
    setDirty(false);
  };

  return (
    <SectionCard title="Tiers"
                 subtitle="T1 is Robin, our AI agent. T2–T4 are humans."
                 dirty={dirty} canManage={canManage}
                 onSave={save} onReset={reset} saving={saving}
                 testid="hd-tiers">
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase text-slate-400">Levels</div>
        <div className="flex flex-wrap gap-2">
          {rows.map((r, i) => (
            <span key={`${r.key}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-700"
                  data-testid={`hd-tier-chip-${r.key}`}>
              <ChevronDown className="h-3 w-3 -rotate-90 opacity-50" />
              {r.key}
              {canManage && (
                <button className="rounded p-0.5 text-violet-500 hover:bg-violet-100" onClick={() => rmRow(i)}
                        aria-label={`remove ${r.key}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {canManage && (
          <div className="mt-2 flex gap-2">
            <Input value={levelsInput} onChange={(e) => setLevelsInput(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLevels(); } }}
                   placeholder="T1, T2, T3, T4" className="max-w-xl"
                   data-testid="hd-tier-levels" />
            <Button size="sm" onClick={addLevels} disabled={!levelsInput.trim()} data-testid="hd-tier-add">
              Add
            </Button>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="mb-1 text-[10px] font-semibold uppercase text-slate-400">Role label</div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {rows.map((r, i) => (
            <div key={`${r.key}-role-${i}`} className="flex items-center gap-2"
                 data-testid={`hd-tier-row-${r.key}`}>
              <div className="w-8 text-xs font-semibold text-violet-700">{r.key}</div>
              <Input className="h-9 flex-1" value={r.role_label}
                     onChange={(e) => patchRow(i, { role_label: e.target.value })}
                     placeholder="Human, Robin AI Agent, …"
                     disabled={!canManage}
                     data-testid={`hd-tier-role-${r.key}`} />
              <label className="ml-1 inline-flex shrink-0 items-center gap-1 text-[11px] text-slate-500">
                <Switch checked={!!r.is_ai} onCheckedChange={(v) => patchRow(i, { is_ai: v })}
                        disabled={!canManage}
                        data-testid={`hd-tier-ai-${r.key}`} />
                AI
              </label>
            </div>
          ))}
        </div>
        <p className="mt-4 border-l-2 border-slate-200 pl-3 text-[11px] italic text-slate-500">
          Robin (T1) is our automated first-touch agent — it reads new tickets, triages, and drafts replies
          inside its RAG scope. Playbooks and auto-close triggers are configured in the Robin panel below.
        </p>
      </div>
    </SectionCard>
  );
}


// -----------------------------------------------------------------------
// Shared UI
// -----------------------------------------------------------------------
function SectionCard({ title, subtitle, children, dirty, canManage, onSave, onReset, saving, testid }) {
  return (
    <Card className="border-slate-200 p-6 shadow-sm" data-testid={testid}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <ChevronDown className="mt-0.5 h-4 w-4 text-slate-400" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
            <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>
          </div>
        </div>
        {canManage && (
          <div className="flex shrink-0 items-center gap-2">
            {dirty && <span className="inline-block rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">unsaved</span>}
            <Button size="sm" onClick={onSave} disabled={!dirty || saving} data-testid={`${testid}-save`}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={onReset} disabled={saving} data-testid={`${testid}-reset`}>
              Reset
            </Button>
          </div>
        )}
      </div>
      {children}
    </Card>
  );
}

function SkeletonPane() {
  return <Card className="border-slate-200 p-6 shadow-sm text-sm text-slate-400">Loading…</Card>;
}

// -----------------------------------------------------------------------
// SLA Policies editor — first-response & resolution minutes per priority.
// Reuses /helpdesk/sla/policies which drives the ticket SLA engine.
// -----------------------------------------------------------------------
export function HelpdeskSlaPoliciesAdmin({ canManage }) {
  const [rows, setRows] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/helpdesk/sla/policies");
    setRows(data.policies || []);
    setDirty(false);
  };
  useEffect(() => { load(); }, []);

  if (!rows) return <SkeletonPane />;

  const patchRow = (i, patch) => {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        priority: r.priority,
        first_response_minutes: Number(r.first_response_minutes) || 0,
        resolution_minutes:     Number(r.resolution_minutes) || 0,
        active: r.active !== false,
      }));
      const { data } = await api.put("/helpdesk/sla/policies", payload);
      setRows(data.policies || []);
      setDirty(false);
      toast.success("SLA policies saved");
    } catch { toast.error("Could not save SLA policies"); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard title="SLA Policies"
                 subtitle="First-response and resolution targets per ticket priority. Countdowns respect the Business Hours calendar."
                 dirty={dirty} canManage={canManage}
                 onSave={save} onReset={load} saving={saving}
                 testid="hd-sla">
      <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold uppercase text-slate-400">
        <div className="col-span-3">Priority</div>
        <div className="col-span-4">First response (min)</div>
        <div className="col-span-4">Resolution (min)</div>
        <div className="col-span-1">Active</div>
      </div>
      <div className="mt-2 space-y-2">
        {rows.map((r, i) => (
          <div key={r.priority || i} className="grid grid-cols-12 items-center gap-2">
            <div className="col-span-3">
              <span className="inline-block rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
                {r.priority}
              </span>
            </div>
            <Input className="col-span-4 h-9" type="number" min={0}
                   value={r.first_response_minutes ?? 0}
                   onChange={(e) => patchRow(i, { first_response_minutes: e.target.value })}
                   disabled={!canManage}
                   data-testid={`hd-sla-fr-${r.priority}`} />
            <Input className="col-span-4 h-9" type="number" min={0}
                   value={r.resolution_minutes ?? 0}
                   onChange={(e) => patchRow(i, { resolution_minutes: e.target.value })}
                   disabled={!canManage}
                   data-testid={`hd-sla-rs-${r.priority}`} />
            <div className="col-span-1">
              <Switch checked={r.active !== false}
                      onCheckedChange={(v) => patchRow(i, { active: v })}
                      disabled={!canManage}
                      data-testid={`hd-sla-active-${r.priority}`} />
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}


// -----------------------------------------------------------------------
// Business Hours editor.
// -----------------------------------------------------------------------
const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const DAY_KEYS   = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export function HelpdeskBusinessHoursAdmin({ canManage }) {
  const [bh, setBh] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/helpdesk/sla/business-hours");
    setBh(data.business_hours || null);
    setDirty(false);
  };
  useEffect(() => { load(); }, []);

  if (!bh) return <SkeletonPane />;

  const setField = (patch) => { setBh({ ...bh, ...patch }); setDirty(true); };
  const setDay = (dayKey, wins) => setField({ week: { ...(bh.week || {}), [dayKey]: wins } });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/helpdesk/sla/business-hours", bh);
      setBh(data.business_hours);
      setDirty(false);
      toast.success("Business hours saved");
    } catch { toast.error("Could not save hours"); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard title="Business Hours"
                 subtitle="When enabled, SLA countdowns pause outside these windows and on holidays."
                 dirty={dirty} canManage={canManage}
                 onSave={save} onReset={load} saving={saving}
                 testid="hd-hours">
      <div className="flex flex-wrap items-center gap-4">
        <label className="inline-flex items-center gap-2 text-xs font-medium">
          <Switch checked={!!bh.enabled} onCheckedChange={(v) => setField({ enabled: v })}
                  disabled={!canManage} data-testid="hd-hours-enabled" />
          Enforce business hours
        </label>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Timezone</span>
          <Input className="h-8 w-52" value={bh.tz || ""}
                 onChange={(e) => setField({ tz: e.target.value })}
                 placeholder="America/Toronto"
                 disabled={!canManage}
                 data-testid="hd-hours-tz" />
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {DAY_KEYS.map((d) => (
          <BhDayRow key={d} dayKey={d} windows={(bh.week && bh.week[d]) || []}
                    canManage={canManage} onChange={(wins) => setDay(d, wins)} />
        ))}
      </div>

      <div className="mt-4">
        <div className="mb-1 text-[10px] font-semibold uppercase text-slate-400">Holidays (YYYY-MM-DD, comma-separated)</div>
        <Input value={(bh.holidays || []).join(", ")}
               onChange={(e) => setField({ holidays: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
               placeholder="2026-07-01, 2026-12-25"
               disabled={!canManage}
               data-testid="hd-hours-holidays" />
      </div>
    </SectionCard>
  );
}

function BhDayRow({ dayKey, windows, onChange, canManage }) {
  const setWin = (idx, patch) => onChange(windows.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  const addWin = () => onChange([...(windows || []), { start: "09:00", end: "17:00" }]);
  const rmWin  = (idx) => onChange(windows.filter((_, i) => i !== idx));
  return (
    <div className="grid grid-cols-12 items-center gap-2">
      <div className="col-span-2 text-xs font-medium text-slate-600">{DAY_LABELS[dayKey]}</div>
      <div className="col-span-9 flex flex-wrap items-center gap-2">
        {(windows || []).length === 0 && <span className="text-xs text-slate-400">Closed</span>}
        {(windows || []).map((w, i) => (
          <div key={i} className="flex items-center gap-1">
            <Input className="h-7 w-20 text-xs" value={w.start}
                   onChange={(e) => setWin(i, { start: e.target.value })}
                   disabled={!canManage}
                   data-testid={`hd-hours-${dayKey}-${i}-start`} />
            <span className="text-xs text-slate-400">–</span>
            <Input className="h-7 w-20 text-xs" value={w.end}
                   onChange={(e) => setWin(i, { end: e.target.value })}
                   disabled={!canManage}
                   data-testid={`hd-hours-${dayKey}-${i}-end`} />
            {canManage && (
              <button className="rounded p-0.5 text-slate-400 hover:text-rose-600" onClick={() => rmWin(i)}
                      aria-label="remove window" data-testid={`hd-hours-${dayKey}-${i}-rm`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="col-span-1">
        {canManage && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={addWin}
                  data-testid={`hd-hours-${dayKey}-add`}>
            <Plus className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// Combined "Admin settings" view — stacks all three sections on a single
// tab.  Used by the "helpdesk-admin" sub-tab which mirrors the
// user-provided design mock.
// -----------------------------------------------------------------------
// -----------------------------------------------------------------------
// Automation editor — the auto-triage confidence gate.
// -----------------------------------------------------------------------
export function HelpdeskAutomationAdmin({ canManage }) {
  const { data, setData } = useHelpdeskSettings();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!data) return <SkeletonPane />;
  const a = data.automation || { auto_triage_enabled: false, auto_triage_min_conf: 0.6 };
  const setField = (patch) => { setData({ ...data, automation: { ...a, ...patch } }); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put("/helpdesk/settings/automation", {
        auto_triage_enabled:  !!a.auto_triage_enabled,
        auto_triage_min_conf: Number(a.auto_triage_min_conf) || 0,
      });
      setData(fresh);
      setDirty(false);
      toast.success("Automation saved");
    } catch { toast.error("Could not save automation"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    const { data: fresh } = await api.get("/helpdesk/settings");
    setData(fresh);
    setDirty(false);
  };

  return (
    <SectionCard title="Automation"
                 subtitle="AI triage confidence gate."
                 dirty={dirty} canManage={canManage}
                 onSave={save} onReset={reset} saving={saving}
                 testid="hd-automation">
      <div className="space-y-4 text-xs">
        <label className="flex items-start gap-3">
          <Switch checked={!!a.auto_triage_enabled} onCheckedChange={(v) => setField({ auto_triage_enabled: v })}
                  disabled={!canManage} data-testid="hd-auto-triage" />
          <div>
            <div className="font-medium text-slate-700">Auto triage on ticket create</div>
            <div className="text-slate-500">
              Fire Claude Sonnet 4.6 in the background as soon as a ticket is
              opened. Classification + reply draft are ready before an agent
              clicks in.
            </div>
          </div>
        </label>
        <div className="flex items-start gap-3 pt-2">
          <div className="pt-1">
            <Input type="number" min={0} max={1} step="0.05"
                   className="h-8 w-20 text-xs"
                   value={a.auto_triage_min_conf ?? 0.6}
                   onChange={(e) => setField({ auto_triage_min_conf: e.target.value })}
                   disabled={!canManage} data-testid="hd-auto-triage-conf" />
          </div>
          <div>
            <div className="font-medium text-slate-700">Auto-triage confidence floor</div>
            <div className="text-slate-500">
              Only accept auto-triage classifications with confidence
              ≥ this value (0.00–1.00). Low-quality guesses are recorded
              on the ticket but <b>not shown</b> to agents. Suggested
              starting point: <b>0.60</b>. Set to <b>0</b> to accept everything.
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

export function HelpdeskDeletedAdmin({ canManage }) {
  const [tickets, setTickets] = useState(null);
  const [retention, setRetention] = useState(365);
  const [busy, setBusy] = useState(null); // ticket_id currently acting on

  const load = async () => {
    const { data } = await api.get("/helpdesk/tickets/deleted");
    setTickets(data.tickets || []);
    if (data.retention_days) setRetention(data.retention_days);
  };
  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    setBusy(t.ticket_id);
    try {
      await api.post(`/helpdesk/tickets/${t.ticket_id}/restore`);
      toast.success(`Restored ${t.cf_id}`);
      await load();
    } catch { toast.error("Restore failed"); }
    finally { setBusy(null); }
  };
  const purge = async (t) => {
    if (!window.confirm(`Permanently delete ticket ${t.cf_id}? This cannot be undone.`)) return;
    setBusy(t.ticket_id);
    try {
      await api.delete(`/helpdesk/tickets/${t.ticket_id}/purge`);
      toast.success(`Purged ${t.cf_id}`);
      await load();
    } catch { toast.error("Purge failed"); }
    finally { setBusy(null); }
  };

  if (tickets === null) return <SkeletonPane />;

  return (
    <Card className="border-slate-200 p-6 shadow-sm" data-testid="hd-trash">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Deleted Tickets</div>
          <div className="mt-0.5 text-sm text-slate-500">
            Soft-deleted tickets awaiting restore or auto-purge. Retention: <b>{retention} days</b> from deletion.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="hd-trash-refresh">Refresh</Button>
      </div>

      {tickets.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
          Trash is empty. Deleted tickets appear here for {retention} days.
        </p>
      )}

      {tickets.length > 0 && (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">CF-ID</th>
                <th className="px-3 py-2 text-left">Subject</th>
                <th className="px-3 py-2 text-left">Requester</th>
                <th className="px-3 py-2 text-left">Deleted by</th>
                <th className="px-3 py-2 text-left">Deleted at</th>
                <th className="px-3 py-2 text-left">Purges on</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const del = new Date(t.deleted_at);
                const purgeAt = new Date(del.getTime() + retention * 86400000);
                return (
                  <tr key={t.ticket_id} className="border-t border-slate-200"
                      data-testid={`hd-trash-row-${t.ticket_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{t.cf_id}</td>
                    <td className="px-3 py-2">{t.subject}</td>
                    <td className="px-3 py-2 text-slate-600">{t.requester_name || t.requester_email}</td>
                    <td className="px-3 py-2 text-slate-600">{t.deleted_by_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-500">{del.toLocaleString()}</td>
                    <td className="px-3 py-2 text-slate-500">{purgeAt.toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-right">
                      {canManage && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                  onClick={() => restore(t)} disabled={busy === t.ticket_id}
                                  data-testid={`hd-trash-restore-${t.ticket_id}`}>
                            Restore
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] border-rose-300 text-rose-600 hover:bg-rose-50"
                                  onClick={() => purge(t)} disabled={busy === t.ticket_id}
                                  data-testid={`hd-trash-purge-${t.ticket_id}`}>
                            Delete forever
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


export default function HelpdeskAdminSettings({ canManage }) {
  return (
    <div className="space-y-4" data-testid="hd-admin-settings">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-800">Admin settings</h2>
        <p className="mt-1 text-sm text-slate-500">
          Departments, priorities, tiers, statuses and platform taxonomy.
        </p>
      </div>
      <HelpdeskDepartmentsAdmin canManage={canManage} />
      <HelpdeskPrioritiesAdmin  canManage={canManage} />
      <HelpdeskTiersAdmin       canManage={canManage} />
    </div>
  );
}
